import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const AdminAccessPortal = ({ isOpen = false, onClose, onAuthenticate }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const isValid = await onAuthenticate(credentials);
      if (isValid) {
        setCredentials({ username: '', password: '' });
        onClose();
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleOverlayClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-3000 flex items-center justify-center bg-background p-4 animate-fade-in"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-portal-title"
    >
      <div className="w-full max-w-md bg-card rounded-xl shadow-2xl p-8 animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 id="admin-portal-title" className="text-xl font-semibold text-foreground">
                Administrative Access
              </h2>
              <p className="text-sm text-muted-foreground">
                Enter credentials to continue
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth focus-ring"
            aria-label="Close portal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Username"
            type="text"
            name="username"
            value={credentials?.username}
            onChange={handleChange}
            placeholder="Enter username"
            required
            disabled={isLoading}
            autoComplete="username"
          />

          <Input
            label="Password"
            type="password"
            name="password"
            value={credentials?.password}
            onChange={handleChange}
            placeholder="Enter password"
            required
            disabled={isLoading}
            autoComplete="current-password"
          />

          {error && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isLoading}
              disabled={isLoading}
              fullWidth
            >
              Authenticate
            </Button>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
            <p>
              This portal provides access to configuration, analytics, and participant management features.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccessPortal;